/** The non-standard event Chromium fires to let a site defer the native install prompt. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

type Listener = () => void

/**
 * Module-scope holder for the browser's deferred install prompt. Chromium frequently fires
 * `beforeinstallprompt` during the initial page load — before React has mounted and the hook's
 * effect has run — so capture happens here, once, as early as `main.tsx` can call it, rather
 * than inside a component effect that would miss an early event.
 */
let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<Listener>()

/** Start listening for the deferred install prompt. Call once, as early as possible. */
export const startInstallPromptCapture = (): void => {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    listeners.forEach((notify) => notify())
  })
}

/** Whether a deferred prompt is currently held (drives the hook's initial state). */
export const hasDeferredPrompt = (): boolean => deferred !== null

/** Drop the held prompt without showing it (e.g. after the app is installed). */
export const clearDeferredPrompt = (): void => {
  deferred = null
}

/** Subscribe to captures that happen after subscription; returns an unsubscribe function. */
export const subscribeDeferredPrompt = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Show the held native prompt and resolve with the user's choice, consuming the prompt (it can
 * only be used once). Resolves `'unavailable'` when no prompt is held.
 */
export const promptDeferredInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
  if (!deferred) return 'unavailable'
  await deferred.prompt()
  const { outcome } = await deferred.userChoice
  deferred = null
  return outcome
}
